import React, { useState, useRef, useContext, forwardRef, useEffect, useImperativeHandle } from 'react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { Mic, Stop, Pause, PlayArrow, Delete, Close } from '@mui/icons-material';
import { ThemeContext } from '../ThemeContext/ThemeContext';
import OpusMediaRecorder from 'opus-media-recorder';
import { toast } from 'react-toastify';

const AudioRecorder = forwardRef(({ onSave, onRecordingStart, onRecordingEnd, onCancel, autoStart = false }, ref) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [previewAudio, setPreviewAudio] = useState(null);
  const [isClosed, setIsClosed] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const { theme } = useContext(ThemeContext);
  const waveformRef = useRef(null);
  const cursorRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const waveformHistoryRef = useRef([]);
  const maxHistoryLength = 1000;
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const sourceRef = useRef(null);
  const audioPlayerRef = useRef(new Audio());
  const audioWaitersRef = useRef([]);
  const [waveformWidth, setWaveformWidth] = useState(0);
  const [isAudioReady, setIsAudioReady] = useState(false);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const handleCanvasClick = (e) => {
    if (!previewAudio || isRecording || !isAudioReady || !duration) return;

    const canvas = waveformRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / canvas.width));
    const newTime = percentage * duration;

    if (isFinite(newTime) && newTime >= 0 && newTime <= duration) {
      audioPlayerRef.current.currentTime = newTime;
      setCurrentTime(newTime);
      updateCursorPosition(newTime);
    }
  };

  const updateCursorPosition = (time) => {
    if (!cursorRef.current || !waveformRef.current || !duration) return;
    const container = waveformRef.current;
    const cursor = cursorRef.current;
    const width = container.offsetWidth;
    const left = Math.max(0, Math.min(width, (time / duration) * width));
    cursor.style.left = `${left}px`;
  };

  const handlePlayPause = () => {
    if (!previewAudio || !isAudioReady) return;

    if (isPlaying) {
      audioPlayerRef.current.pause();
      setIsPlaying(false);
    } else {
      audioPlayerRef.current.play().catch(error => {
        console.error('Erro ao reproduzir áudio:', error);
        setIsPlaying(false);
      });
      setIsPlaying(true);
    }
  };

  const drawWaveform = () => {
    if (!analyserRef.current) return;

    const canvas = waveformRef.current;
    const ctx = canvas.getContext("2d");
    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(dataArray);

    if (!isPaused) {
      const average = Array.from(dataArray).reduce((a, b) => a + b, 0) / dataArray.length;
      waveformHistoryRef.current.push(average);
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const history = waveformHistoryRef.current;
    if (history.length === 0) return;

    // Calcular a largura total necessária para todas as barras
    const totalWidth = history.length * 3; // 3 pixels por barra
    const scale = canvas.width / totalWidth;

    // Desenhar todas as barras, escalando conforme necessário
    history.forEach((value, index) => {
      const x = index * 3 * scale;
      const barWidth = 2 * scale;
      const barHeight = (value / 255) * (canvas.height / 1.5);
      const centerY = canvas.height / 2;

      // Cores mais vibrantes e contrastantes
      const gradient = ctx.createLinearGradient(x, centerY - barHeight, x, centerY + barHeight);
      gradient.addColorStop(0, theme.palette.primary.light); // Verde brilhante
      gradient.addColorStop(0.5, theme.palette.primary.main); // Verde neon
      gradient.addColorStop(1, theme.palette.primary.dark); // Verde brilhante

      ctx.fillStyle = gradient;

      // Desenhar barra com bordas arredondadas
      const radius = barWidth / 2;
      ctx.beginPath();
      ctx.moveTo(x + radius, centerY - barHeight);
      ctx.lineTo(x + barWidth - radius, centerY - barHeight);
      ctx.quadraticCurveTo(x + barWidth, centerY - barHeight, x + barWidth, centerY - barHeight + radius);
      ctx.lineTo(x + barWidth, centerY + barHeight - radius);
      ctx.quadraticCurveTo(x + barWidth, centerY + barHeight, x + barWidth - radius, centerY + barHeight);
      ctx.lineTo(x + radius, centerY + barHeight);
      ctx.quadraticCurveTo(x, centerY + barHeight, x, centerY + barHeight - radius);
      ctx.lineTo(x, centerY - barHeight + radius);
      ctx.quadraticCurveTo(x, centerY - barHeight, x + radius, centerY - barHeight);
      ctx.closePath();
      ctx.fill();

      // Adicionar borda para maior contraste
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 0.5;
      ctx.stroke();
    });

    // Só continua a animação se não estiver pausado
    if (!isPaused) {
      animationFrameRef.current = requestAnimationFrame(drawWaveform);
    }
  };

  const startRecording = async () => {
    // navigator.mediaDevices só existe em contexto seguro (HTTPS ou localhost).
    // Em produção via http o microfone fica indisponível — avisa em vez de quebrar.
    if (!navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== 'function') {
      toast.error('Gravação de áudio indisponível: o navegador só libera o microfone em conexão segura (HTTPS) ou em localhost.');
      onCancel && onCancel();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const workerOptions = {
        encoderWorkerFactory: () =>
          new Worker(`${process.env.PUBLIC_URL}/opus-media-recorder/encoderWorker.umd.js`),
        OggOpusEncoderWasmPath: `${process.env.PUBLIC_URL}/opus-media-recorder/OggOpusEncoder.wasm`,
        WebMOpusEncoderWasmPath: `${process.env.PUBLIC_URL}/opus-media-recorder/WebMOpusEncoder.wasm`
      };

      const mediaRecorder = new OpusMediaRecorder(stream, {
        mimeType: 'audio/ogg; codecs=opus',
      }, workerOptions);

      chunksRef.current = [];
      waveformHistoryRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/ogg' });
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64Audio = reader.result;
          setPreviewAudio(base64Audio);
          // Resolver quaisquer aguardantes
          if (audioWaitersRef.current.length) {
            audioWaitersRef.current.forEach(resolve => resolve(base64Audio));
            audioWaitersRef.current = [];
          }
        };
        reader.readAsDataURL(blob);
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();

      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.3;

      source.connect(analyser);
      analyserRef.current = analyser;
      audioContextRef.current = audioCtx;
      sourceRef.current = source;

      const canvas = waveformRef.current;
      const ctx = canvas.getContext("2d");

      const resizeCanvas = () => {
        const container = canvas.parentElement;
        canvas.width = container.clientWidth;
        canvas.height = 60;
        setWaveformWidth(canvas.width);
      };

      resizeCanvas();
      window.addEventListener('resize', resizeCanvas);

      drawWaveform();
      onRecordingStart && onRecordingStart();
      setIsRecording(true);
      setIsPaused(false);
    } catch (err) {
      console.error('Erro ao acessar microfone:', err);
      toast.error('Não foi possível acessar o microfone. Verifique as permissões do navegador.');
      onCancel && onCancel();
    }
  };

  const pauseRecording = () => {
    if (!mediaRecorderRef.current) return;
    if (isPaused) {
      mediaRecorderRef.current.resume();
      audioContextRef.current.resume();
      // Reinicia a animação quando retomar
      drawWaveform();
      setIsPaused(false);
    } else {
      mediaRecorderRef.current.pause();
      audioContextRef.current.suspend();
      // Cancela a animação quando pausar
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      setIsPaused(true);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().catch(() => {});
    }
    if (sourceRef.current) {
      sourceRef.current.disconnect();
    }
    setIsRecording(false);
    setIsPaused(false);
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    waveformHistoryRef.current = [];
    onRecordingEnd && onRecordingEnd();
  };

  const handleSave = () => {
    if (isRecording) {
      stopRecording();
    }
    if (previewAudio) {
      onSave && onSave(previewAudio);
    }
  };

  const resetRecording = () => {
    setPreviewAudio(null);
    chunksRef.current = [];
    mediaRecorderRef.current = null;
    setIsRecording(false);
    setIsPaused(false);
    setIsClosed(true);
    waveformHistoryRef.current = [];
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    onCancel && onCancel();
  };

  useEffect(() => {
    if (autoStart) {
      startRecording();
      setIsClosed(false);
    }
  }, [autoStart]);

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().catch(() => {});
      }
    };
  }, []);

  useEffect(() => {
    if (previewAudio) {
      const audio = audioPlayerRef.current;
      audio.src = previewAudio;

      const handleLoadedMetadata = () => {
        if (isFinite(audio.duration)) {
          setDuration(audio.duration);
          setIsAudioReady(true);
        }
      };

      const handleTimeUpdate = () => {
        if (isFinite(audio.currentTime)) {
          setCurrentTime(audio.currentTime);
          updateCursorPosition(audio.currentTime);
        }
      };

      const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
        updateCursorPosition(0);
      };

      const handleError = (error) => {
        console.error('Erro no áudio:', error);
        setIsAudioReady(false);
        setIsPlaying(false);
      };

      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('error', handleError);

      return () => {
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('error', handleError);
        audio.pause();
        audio.src = '';
      };
    }
  }, [previewAudio]);

  useImperativeHandle(ref, () => ({
    getCurrentAudio: () => {
      return previewAudio;
    },
    getAudioAfterStop: () => {
      if (previewAudio) return Promise.resolve(previewAudio);
      if (isRecording && mediaRecorderRef.current) {
        return new Promise((resolve) => {
          audioWaitersRef.current.push(resolve);
          stopRecording();
        });
      }
      return Promise.resolve(null);
    },
    resetRecording,
    handleSave,
    stopRecording
  }));

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: '12px',
        backgroundColor: theme.palette.background.secondary,
        boxShadow: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
        maxWidth: '100%',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
        <IconButton
          color="default"
          onClick={resetRecording}
          sx={{
            backgroundColor: theme.palette.error.main,
            '&:hover': {
              backgroundColor: theme.palette.error.dark,
            }
          }}
        >
          {isClosed ? <Close /> : <Delete />}
        </IconButton>

        <Box sx={{
          flexGrow: 1,
          maxWidth: 'calc(100% - 200px)',
          height: '60px',
          backgroundColor: theme.palette.background.secondary,
          borderRadius: '4px',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          border: isRecording ? `2px solid ${theme.palette.primary.main}` : '1px solid transparent',
          transition: 'all 0.3s ease',
          boxShadow: isRecording ? `0 0 10px ${theme.palette.primary.main}40` : 'none'
        }}>
          <canvas
            ref={waveformRef}
            onClick={handleCanvasClick}
            style={{
              width: '100%',
              height: '100%',
              display: 'block',
              cursor: previewAudio ? 'pointer' : 'default'
            }}
          />
          {previewAudio && (
            <Box
              ref={cursorRef}
              sx={{
                position: 'absolute',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: theme.palette.primary.main,
                pointerEvents: 'none',
                transition: 'left 0.05s linear',
              }}
            />
          )}
          {isRecording && !isPaused && (
            <Box sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              backgroundColor: 'rgba(211, 47, 47, 0.9)',
              color: 'white',
              padding: '4px 8px',
              borderRadius: '12px',
              fontSize: '0.7rem',
              fontWeight: 'bold',
              zIndex: 10,
              animation: 'pulse 1.5s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.7 }
              }
            }}>
              <Box sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: 'white',
                animation: 'blink 1s ease-in-out infinite',
                '@keyframes blink': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.3 }
                }
              }} />
              Gravando
            </Box>
          )}
        </Box>

        {!isRecording && !previewAudio && (
          <IconButton
            color="primary"
            onClick={startRecording}
            sx={{
              backgroundColor: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              }
            }}
          >
            <Mic />
          </IconButton>
        )}
        {isRecording && (
          <>
            <IconButton
              color="warning"
              onClick={pauseRecording}
              sx={{
                backgroundColor: theme.palette.warning.main,
                '&:hover': {
                  backgroundColor: theme.palette.warning.dark,
                }
              }}
            >
              {isPaused ? <PlayArrow /> : <Pause />}
            </IconButton>
            <IconButton
              color="error"
              onClick={stopRecording}
              sx={{
                backgroundColor: theme.palette.error.main,
                '&:hover': {
                  backgroundColor: theme.palette.error.dark,
                }
              }}
            >
              <Stop />
            </IconButton>
          </>
        )}
        {previewAudio && !isRecording && (
          <>
            <IconButton
              color="primary"
              onClick={handlePlayPause}
              sx={{
                backgroundColor: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                }
              }}
            >
              {isPlaying ? <Pause /> : <PlayArrow />}
            </IconButton>
            <Box sx={{ minWidth: '60px', textAlign: 'center' }}>
              <Typography variant="caption" color="textSecondary">
                {isPlaying ? formatTime(currentTime) : formatTime(duration)}
              </Typography>
            </Box>
          </>
        )}
      </Stack>
    </Box>
  );
});

export default AudioRecorder;